import React, { memo, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { PATH_JOINER } from '../utils/getConfigBitPath'
import { selectConfigBit, selectConfigIsChecked } from '../../../store/selectors/configSelectors'
import { useFormValidation } from '../../../hooks/useFormValidation'
import {
    inputWithSuggestionsDropdown,
    buildControlsExt,
} from '../../inputs/controls'
import { inputValidationCheck } from '../../../validators/inputValidators/inputValidators'
import { stringInputErrors } from '../../../utils/validationErrors/stringInputErrors'
import { initInputData } from '../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../utils/InputDataDispatcher'
import { getLabel } from '../../../utils/getLabel'

export interface ISuggestedValuesProps {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: any
    name: string
    wrapper?: string
}

export const SuggestedValues = memo(function SuggestedValues({
    dataPath,
    pathWithElementID,
    dataSchema,
    name,
    wrapper,
}: ISuggestedValuesProps){
    const dispatch = useAppDispatch()
    const configBit = useAppSelector(selectConfigBit(dataPath.split(PATH_JOINER)))
    const path = [...(dataPath.split(PATH_JOINER)), name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectConfigIsChecked)
    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        default: defaultValue,
    } = dataSchema
    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck })
    const [guid, , tabName] = path
    const suggestedValuesPath = dataSchema.suggested_values_path ?? ''
    const suggestedValuesPathArr = suggestedValuesPath.split('.')
    const suggestedValuesPathFull = [guid, 'configuration', ...suggestedValuesPathArr]
    const suggestedValuesItems = useAppSelector(selectConfigBit(suggestedValuesPathFull))

    const label: string = getLabel(name, inputLabel)
    const className: string = !wrapper ? 'w-50 pe-2' : ''
    const errorKey: string = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const errMsg = stringInputErrors(inputState?.[name], dataSchema)
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined

    const values = Array.isArray(suggestedValuesItems)
        ? suggestedValuesItems.reduce((acc, item) => {
              if (item && item.rule_name) acc.push(item.rule_name)
              return acc
          }, [])
        : []

    const options = Array.from(new Set(values)) as unknown as string[]

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    guid,
                    errorKey,
                    data: configBit,
                    setInputState,
                    tabName,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isChecked[guid] && dataPath.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    guid,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked[guid]]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                guid,
                errorKey,
                isUndefined,
                checkPath,
                type,
            },
            dispatch
        )
        setTouched && setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors =  [
        inputWithSuggestionsDropdown({
            name,
            options,
            errMsg,
            tooltipText,
            placeholder: defaultValue,
            label,
            className,
            isColumn: true,
            isRequired,
            onBlur: handleOnBlur,
        }),
    ]

    const SuggestedValuesCombo = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{SuggestedValuesCombo}</div>
})